import type { FormEvent } from "react";
import { useState } from "react";

import { Icon } from "../components/common/Icon";
import { PageHero } from "../components/common/PageHero";
import { SmartLink } from "../components/common/SmartLink";
import { useDocumentMeta } from "../lib/useDocumentMeta";

const trustPoints = [
  "AI security specialists",
  "Offensive security expertise",
  "Compliance-focused solutions",
  "Executive-level strategic guidance",
  "Tailored security programs"
];

const interestOptions = [
  "Secure AI Implementation",
  "RapidFactory",
  "KyberDocs",
  "ODAN",
  "Mawforge",
  "MSSP Services",
  "Not sure yet"
];

type SubmitState =
  | { kind: "idle" }
  | { kind: "submitting" }
  | { kind: "success"; message: string }
  | { kind: "error"; message: string };

export function ContactPage() {
  const [submitState, setSubmitState] = useState<SubmitState>({ kind: "idle" });

  useDocumentMeta({
    canonicalPath: "/contact",
    description:
      "Contact Holocron Security for secure AI implementation, CMMC environments, RapidFactory, KyberDocs, ODAN, Mawforge, or managed security services.",
    title: "Contact | Holocron Security"
  });

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    if (!form.reportValidity()) {
      return;
    }

    setSubmitState({ kind: "submitting" });
    try {
      const response = await fetch("/api/contact", {
        body: JSON.stringify(formPayload(form)),
        headers: { "content-type": "application/json" },
        method: "POST"
      });

      if (!response.ok) {
        throw new Error("Contact request failed.");
      }

      form.reset();
      setSubmitState({ kind: "success", message: "Thanks. Your request was sent to Holocron Security." });
    } catch {
      setSubmitState({
        kind: "error",
        message: "The request could not be sent. Email sales@holocronsecurity.com directly."
      });
    }
  }

  const isSubmitting = submitState.kind === "submitting";

  return (
    <>
      <PageHero
        copy="Tell us where you are trying to add secure AI, compliant infrastructure, evidence, or managed security capacity. We will route the request to the right Holocron team."
        eyebrow="Contact"
        title="Start with the mission outcome."
      />

      <section className="surface-dark">
        <div className="section-shell contact-layout">
          <form
            action="/api/contact"
            className="contact-form"
            encType="application/x-www-form-urlencoded"
            method="post"
            onSubmit={(event) => void handleSubmit(event)}
          >
            <div className="form-section-heading">
              <p className="section-kicker">Customer intake</p>
              <h2>Help us understand the request.</h2>
            </div>

            <label aria-hidden="true" className="form-honeypot">
              Website
              <input autoComplete="off" name="website" tabIndex={-1} type="text" />
            </label>

            <div className="form-grid">
              <label>
                Full Name
                <input name="fullName" placeholder="Jane Smith" required type="text" />
              </label>
              <label>
                Business Email
                <input name="email" placeholder="jane@company.com" required type="email" />
              </label>
              <label>
                Company
                <input name="company" placeholder="Company name" type="text" />
              </label>
              <label>
                Phone
                <input name="phone" placeholder="Optional" type="tel" />
              </label>
              <label>
                Primary Interest
                <select defaultValue="" name="interest" required>
                  <option disabled value="">
                    Select an option
                  </option>
                  {interestOptions.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Timeline
                <select defaultValue="" name="timeline">
                  <option disabled value="">
                    Select timeline
                  </option>
                  <option>Immediately</option>
                  <option>Within 30 days</option>
                  <option>Within 90 days</option>
                  <option>Within 6 months</option>
                  <option>Exploring options</option>
                </select>
              </label>
            </div>

            <label>
              What challenge are you trying to solve?
              <textarea
                name="challenge"
                placeholder="Describe the workflow, environment, compliance driver, or security objective."
                rows={6}
              />
            </label>

            {submitState.kind === "success" ? (
              <p className="form-status form-status--success" role="status">
                {submitState.message}
              </p>
            ) : null}
            {submitState.kind === "error" ? (
              <p className="form-status form-status--error" role="alert">
                {submitState.message}
              </p>
            ) : null}

            <button className="button primary" disabled={isSubmitting} type="submit">
              {isSubmitting ? "Sending..." : "Send Questionnaire"}
            </button>
          </form>

          <aside className="contact-trust-panel">
            <div className="icon-badge icon-badge--glass">
              <Icon name="shield" />
            </div>
            <h2>Why organizations choose Holocron Security</h2>
            <ul>
              {trustPoints.map((point) => (
                <li key={point}>{point}</li>
              ))}
            </ul>
            <div className="contact-direct">
              <p>Prefer direct email?</p>
              <SmartLink className="inline-link" href="mailto:sales@holocronsecurity.com">
                sales@holocronsecurity.com
              </SmartLink>
            </div>
          </aside>
        </div>
      </section>
    </>
  );
}

function formPayload(form: HTMLFormElement): Record<string, string> {
  const payload: Record<string, string> = {};
  for (const [key, value] of new FormData(form).entries()) {
    if (typeof value === "string") {
      payload[key] = value;
    }
  }
  return payload;
}
